import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Printer } from "@semo/icons";
import type { CostEstimateData, MidWork } from "../types";
import {
  type CostEstimateResult,
  compositionPct,
  formatRate,
  formatWon,
  itemBreakdown,
  majorWorkBreakdown,
  mergeWorks,
  midWorkBreakdown,
} from "../calc";
import { downloadExcel, sheetCount, type ExportMeta } from "../excelExport";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  data: CostEstimateData;
  result: CostEstimateResult;
  projectName: string;
  vendorName: string;
  onChangeProjectName: (v: string) => void;
  onChangeVendorName: (v: string) => void;
}

const CIRCLED = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const circled = (i: number) =>
  i < CIRCLED.length ? CIRCLED.charAt(i) : `${i + 1}`;

const PRIMARY_CTA = "bg-primary text-primary-foreground hover:bg-primary-hover";

/**
 * 인쇄(PDF 저장) 시 미리보기 문서 영역(.ce-print-area)만 보이도록 하는 CSS.
 * 모달이 열려 있을 때만 DOM에 주입된다.
 */
const PRINT_CSS = `
@media print {
  /* 포털(인쇄 영역) 외 모든 것 숨김 → 모달 테두리/버튼/화면 제외 */
  body > *:not(.ce-print-portal) { display: none !important; }
  /* 용지 규격을 A4 + 여백으로 고정 (누가 뽑아도 일정하게) */
  @page { size: A4; margin: 12mm; }
  .ce-print-portal {
    display: block !important;
    background: #fff !important;
  }
  /* 각 문서(시트)는 새 페이지에서 시작 — 표지·공사원가계산서·각 집계표가 각자 A4 */
  .ce-print-sheet {
    break-before: page;
    page-break-before: always;
  }
  .ce-print-sheet:first-child {
    break-before: auto;
    page-break-before: auto;
  }
  /* 한 페이지보다 긴 표(공사원가계산서)는 '행 단위'로만 분할 → 행 중간에서 안 잘림 */
  .ce-print-sheet tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* 표가 페이지를 넘어가면 머리글(헤더) 반복 */
  .ce-print-sheet thead { display: table-header-group; }
  /* 공사원가계산서(요약표)는 한 페이지에 들어가도록 글씨·여백 축소 */
  .ce-fit-page { font-size: 12px !important; }
  .ce-fit-page table { font-size: 12px !important; }
  .ce-fit-page h2 { font-size: 15px !important; }
  .ce-fit-page td, .ce-fit-page th {
    padding-top: 3px !important;
    padding-bottom: 3px !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  /* 집계표: 품명을 한 줄로 유지(줄바꿈 금지) → A4에서도 화면처럼 가독성 좋게 */
  .ce-agg { font-size: 11px !important; }
  .ce-agg h2 { font-size: 15px !important; }
  .ce-agg td, .ce-agg th {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
  .ce-agg td:first-child, .ce-agg th:first-child {
    white-space: nowrap !important;
  }
  html, body {
    height: auto !important;
    overflow: visible !important;
    background: #fff !important;
  }
}
`;

/** 집계표 공용 (대공사 / 중공종) */
function AggTable({
  title,
  subtitle,
  rows,
  total,
}: {
  title: string;
  subtitle: string;
  rows: {
    name: string;
    unit: string;
    qty: number | string;
    material: number;
    labor: number;
    expense: number;
    total: number;
  }[];
  total: { material: number; labor: number; expense: number; total: number };
}) {
  const cell = "border border-slate-300 px-2 py-1.5";
  return (
    <div className="ce-agg mx-auto max-w-4xl rounded-lg bg-white p-8 shadow-sm">
      <h2 className="text-center text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mb-5 mt-1 text-center text-xs text-slate-500">{subtitle}</p>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs text-slate-600">
            <th className={`${cell} text-left`}>품명</th>
            <th className={`${cell} w-16 text-center`}>규격</th>
            <th className={`${cell} w-12 text-center`}>단위</th>
            <th className={`${cell} w-14 text-right`}>수량</th>
            <th className={`${cell} w-28 text-right`}>재료비 (원)</th>
            <th className={`${cell} w-28 text-right`}>노무비 (원)</th>
            <th className={`${cell} w-28 text-right`}>경비 (원)</th>
            <th className={`${cell} w-28 text-right`}>합계 (원)</th>
            <th className={`${cell} w-16 text-left`}>비고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className={`${cell} text-left text-slate-800`}>{r.name}</td>
              <td className={`${cell} text-center text-slate-400`}>-</td>
              <td className={`${cell} text-center text-slate-600`}>{r.unit}</td>
              <td className={`${cell} text-right tabular-nums`}>{r.qty}</td>
              <td className={`${cell} text-right tabular-nums`}>
                {r.material ? formatWon(r.material) : "-"}
              </td>
              <td className={`${cell} text-right tabular-nums`}>
                {r.labor ? formatWon(r.labor) : "-"}
              </td>
              <td className={`${cell} text-right tabular-nums`}>
                {r.expense ? formatWon(r.expense) : "-"}
              </td>
              <td className={`${cell} text-right font-semibold tabular-nums`}>
                {formatWon(r.total)}
              </td>
              <td className={cell} />
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold">
            <td className={`${cell} text-center`} colSpan={4}>
              합 계
            </td>
            <td className={`${cell} text-right tabular-nums`}>
              {formatWon(total.material)}
            </td>
            <td className={`${cell} text-right tabular-nums`}>
              {formatWon(total.labor)}
            </td>
            <td className={`${cell} text-right tabular-nums`}>
              {formatWon(total.expense)}
            </td>
            <td className={`${cell} text-right tabular-nums text-primary`}>
              {formatWon(total.total)}
            </td>
            <td className={cell} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** ① 내역서 (표지) */
function CoverSheet({
  result,
  projectName,
  vendorName,
  dateStr,
  onChangeProjectName,
  onChangeVendorName,
}: {
  result: CostEstimateResult;
  projectName: string;
  vendorName: string;
  dateStr: string;
  onChangeProjectName: (v: string) => void;
  onChangeVendorName: (v: string) => void;
}) {
  const cell = "border border-slate-300 px-3 py-2.5";
  return (
    <div className="mx-auto max-w-3xl rounded-lg bg-white p-10 shadow-sm">
      <p className="text-center text-xs tracking-widest text-slate-400">
        SEMO SELLER CENTER
      </p>
      <h1 className="mb-1 mt-1 text-center text-3xl font-semibold tracking-[0.4em] text-slate-900">
        공사원가계산서
      </h1>
      <div className="mx-auto mb-8 h-1 w-full max-w-2xl bg-slate-900" />

      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr>
            <td className={`${cell} w-32 bg-slate-50 font-semibold`}>
              공 사 명
            </td>
            <td className={cell}>
              <input
                value={projectName}
                onChange={(e) => onChangeProjectName(e.target.value)}
                placeholder="공사명을 입력하세요"
                className="w-full bg-transparent outline-none placeholder:text-slate-300"
              />
            </td>
          </tr>
          <tr>
            <td className={`${cell} bg-slate-50 font-semibold`}>공 사 금 액</td>
            <td className={`${cell} font-semibold text-primary`}>
              {formatWon(result.totalCost)} 원
            </td>
          </tr>
          <tr>
            <td className={`${cell} bg-slate-50 font-semibold`}>
              공 급 자 (업체명)
            </td>
            <td className={cell}>
              <input
                value={vendorName}
                onChange={(e) => onChangeVendorName(e.target.value)}
                placeholder="업체명을 입력하세요"
                className="w-full bg-transparent outline-none placeholder:text-slate-300"
              />
            </td>
          </tr>
          <tr>
            <td className={`${cell} bg-slate-50 font-semibold`}>작 성 일 자</td>
            <td className={cell}>{dateStr}</td>
          </tr>
        </tbody>
      </table>

      {/* 서명 + 도장 */}
      <div className="mt-10 flex items-end justify-end gap-4">
        <div className="text-right">
          <span className="mr-2 text-sm text-slate-500">공 급 자</span>
          <span className="inline-block w-40 border-b border-slate-400 align-bottom" />
        </div>
        <div className="flex h-20 w-20 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
          도장
        </div>
      </div>
    </div>
  );
}

/** ② 공사원가계산서 (요약표) */
function SummarySheet({
  data,
  result,
  dateStr,
}: {
  data: CostEstimateData;
  result: CostEstimateResult;
  dateStr: string;
}) {
  const cell = "border border-slate-300 px-3 py-2";
  const pct = (v: number) => compositionPct(v, result.netCost);
  return (
    <div className="mx-auto max-w-3xl rounded-lg bg-white p-8 shadow-sm">
      <h2 className="text-center text-lg font-semibold text-slate-900">
        공사원가계산서
      </h2>
      <p className="mb-5 mt-1 text-center text-xs text-slate-500">
        작성일자: {dateStr}
      </p>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs text-slate-600">
            <th className={`${cell} text-left`}>비목</th>
            <th className={`${cell} w-32 text-right`}>금액 (원)</th>
            <th className={`${cell} w-16 text-right`}>구성비</th>
            <th className={`${cell} text-left`}>비고</th>
          </tr>
        </thead>
        <tbody>
          <SectionTr label="재료비" />
          <DataTr
            name="직접재료비"
            amount={result.directMaterial}
            comp={pct(result.directMaterial)}
          />
          <DataTr
            name="간접재료비"
            amount={result.indirectMaterial}
            comp={pct(result.indirectMaterial)}
            note={`직접재료비×${formatRate(data.indirectMaterialRate)}`}
          />
          <DataTr
            bold
            name="[소계]"
            amount={result.materialSubtotal}
            comp={pct(result.materialSubtotal)}
          />

          <SectionTr label="노무비" />
          <DataTr
            name="직접노무비"
            amount={result.directLabor}
            comp={pct(result.directLabor)}
          />
          <DataTr
            name="간접노무비"
            amount={result.indirectLabor}
            comp={pct(result.indirectLabor)}
            note={`직접노무비×${formatRate(data.indirectLaborRate)}`}
          />
          <DataTr
            bold
            name="[소계]"
            amount={result.laborSubtotal}
            comp={pct(result.laborSubtotal)}
          />

          <SectionTr label="경비" />
          {result.expenses.map((exp) => (
            <DataTr
              key={exp.id}
              name={exp.name}
              amount={exp.amount}
              note={`${exp.baseLabel}×${formatRate(exp.rate)}`}
            />
          ))}
          <DataTr bold name="[소계]" amount={result.expenseSubtotal} />
          <DataTr bold name="계 (합계)" amount={result.netCost} comp="100.0%" />

          <DataTr
            name="일반관리비"
            amount={result.generalAdmin}
            note={`계×${formatRate(data.generalAdminRate)}`}
          />
          <DataTr
            name="이윤"
            amount={result.profit}
            note={`(노무+경비+일관)×${formatRate(data.profitRate)}`}
          />
          <DataTr
            name="폐기물처리비"
            amount={result.wasteDisposal}
            note="합계제외"
          />
          <DataTr bold name="공급가액" amount={result.supplyPrice} />
          <DataTr
            name="부가가치세"
            amount={result.vat}
            note={`공급가액×${formatRate(data.vatRate)}`}
          />
          <tr className="bg-primary/5 font-semibold text-primary">
            <td className={cell}>도급액 / 총공사비</td>
            <td className={`${cell} text-right tabular-nums`}>
              {formatWon(result.contractPrice)}
            </td>
            <td className={cell} />
            <td className={`${cell} text-right tabular-nums`}>
              {formatWon(result.totalCost)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const SUMMARY_CELL = "border border-slate-300 px-3 py-2";
function SectionTr({ label }: { label: string }) {
  return (
    <tr className="bg-slate-50 font-semibold text-slate-700">
      <td className={SUMMARY_CELL} colSpan={4}>
        {label}
      </td>
    </tr>
  );
}
function DataTr({
  name,
  amount,
  comp,
  note,
  bold,
}: {
  name: string;
  amount: number;
  comp?: string;
  note?: string;
  bold?: boolean;
}) {
  return (
    <tr className={bold ? "bg-slate-50 font-semibold text-primary" : ""}>
      <td className={`${SUMMARY_CELL} ${bold ? "pl-3" : "pl-6"}`}>{name}</td>
      <td className={`${SUMMARY_CELL} text-right tabular-nums`}>
        {formatWon(amount)}
      </td>
      <td className={`${SUMMARY_CELL} text-right tabular-nums`}>
        {comp ?? ""}
      </td>
      <td className={`${SUMMARY_CELL} text-xs text-slate-500`}>{note ?? ""}</td>
    </tr>
  );
}

export function PreviewModal({
  open,
  onClose,
  data,
  result,
  projectName,
  vendorName,
  onChangeProjectName,
  onChangeVendorName,
}: PreviewModalProps) {
  const today = new Date();
  const dateStr = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일`;

  // 집계표는 재료비+노무비를 같은 중공사로 합쳐서 표시
  const majors = mergeWorks(data.directMaterial, data.directLabor);

  // 탭 구성: 내역서, 공사원가계산서, (대공사 전체 + 그 중공종들)...
  const tabs: { key: string; label: string }[] = [
    { key: "cover", label: "내역서" },
    { key: "summary", label: "공사원가계산서" },
  ];
  for (const major of majors) {
    tabs.push({
      key: `major-${major.code}`,
      label: `${major.code} 집계표(전체)`,
    });
    for (const mid of major.midWorks) {
      tabs.push({
        key: `mid-${major.code}-${mid.code}`,
        label: `${mid.code} 집계표`,
      });
    }
  }

  const [active, setActive] = useState("cover");

  // PDF 저장 = 브라우저 인쇄(→ "PDF로 저장"). 문서 영역만 인쇄되도록 PRINT_CSS가 처리.
  const handlePrintPdf = () => {
    const prevTitle = document.title;
    document.title = `공사원가계산서_${projectName || "견적"}`;
    const restore = () => {
      document.title = prevTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  if (!open) return null;

  const meta: ExportMeta = { projectName, vendorName, dateStr };

  const renderSheet = (key: string) => {
    if (key === "cover")
      return (
        <CoverSheet
          result={result}
          projectName={projectName}
          vendorName={vendorName}
          dateStr={dateStr}
          onChangeProjectName={onChangeProjectName}
          onChangeVendorName={onChangeVendorName}
        />
      );
    if (key === "summary")
      return <SummarySheet data={data} result={result} dateStr={dateStr} />;

    if (key.startsWith("major-")) {
      const code = key.slice("major-".length);
      const major = majors.find((m) => m.code === code);
      if (!major) return null;
      return (
        <AggTable
          title={`${major.code} ${major.name} 공종별 집계표`}
          subtitle={`작성일자: ${dateStr}`}
          rows={major.midWorks.map((mid: MidWork) => {
            const b = midWorkBreakdown(mid);
            return {
              name: `${mid.code} ${mid.name}`,
              unit: "식",
              qty: 1,
              material: b.material,
              labor: b.labor,
              expense: b.expense,
              total: b.total,
            };
          })}
          total={majorWorkBreakdown(major)}
        />
      );
    }

    if (key.startsWith("mid-")) {
      const rest = key.slice("mid-".length);
      const dash = rest.indexOf("-");
      const majorCode = rest.slice(0, dash);
      const midCode = rest.slice(dash + 1);
      const major = majors.find((m) => m.code === majorCode);
      const mid = major?.midWorks.find((w) => w.code === midCode);
      if (!major || !mid) return null;
      return (
        <AggTable
          title={`${mid.code} ${mid.name} 공종별 집계표`}
          subtitle={`${major.code} ${major.name} · 작성일자: ${dateStr}`}
          rows={mid.items.map((item) => {
            const b = itemBreakdown(item);
            return {
              name: item.name,
              unit: item.unit,
              qty: formatWon(item.quantity),
              material: b.material,
              labor: b.labor,
              expense: b.expense,
              total: b.total,
            };
          })}
          total={midWorkBreakdown(mid)}
        />
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40 p-4">
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            공사원가계산서 미리보기
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrintPdf}
              className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${PRIMARY_CTA}`}
            >
              <Printer className="h-4 w-4" /> PDF 저장
            </button>
            <button
              type="button"
              onClick={() => downloadExcel(data, result, meta)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${PRIMARY_CTA}`}
            >
              <Download className="h-4 w-4" /> 엑셀 다운로드 ({sheetCount(data)}
              시트)
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              aria-label="닫기"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 탭바 */}
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-3 pt-2">
          {tabs.map((tab, i) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className={`-mb-px flex shrink-0 items-center gap-1 whitespace-nowrap border-b-2 px-3 py-2 text-xs transition-colors ${
                active === tab.key
                  ? "border-b-primary font-semibold text-primary"
                  : "border-b-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {circled(i)} {tab.label}
            </button>
          ))}
        </div>

        {/* 화면 내용: 현재 선택한 탭만 표시 */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-100 p-6">
          {renderSheet(active)}
        </div>
      </div>

      {/*
        인쇄(PDF) 전용: 모든 시트를 한 문서로.
        모달(fixed) 밖, 문서 최상위(body)로 포털 → 정상 흐름이라 여러 페이지로 잘 넘어감.
        화면엔 숨김(hidden), 인쇄 시에만 표시.
      */}
      {createPortal(
        <div className="ce-print-portal hidden" aria-hidden="true">
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={`ce-print-sheet${
                tab.key === "summary" ? " ce-fit-page" : ""
              }`}
            >
              {renderSheet(tab.key)}
            </div>
          ))}
        </div>,
        document.body,
      )}
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
    </div>
  );
}
